'use client'

import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStoryboard } from '../context'
import SegmentGenerationSection from './SegmentGenerationSection'
import PromptDock from '@/features/workspace/PromptDock'
import ModelFormRouter, { type ModelFormController } from '@/features/workspace/ModelFormRouter'
import useModelStore, { getModelConfig } from '@/client/store/modelStore'
import type { ModelItem } from '@/client/store/modelStore'
import { SUPPORTED_MODEL_KEYS } from '../hooks/storyboardGenerationSupport'
import { applyModelDefaultsToPayload } from '@/client/utils/generationPayload'
import { getAdapter } from '@/client/adapters/registry'
import { getPromptValue } from '@/features/models/shared/promptUtils'
import { buildModelCatalog } from '@/client/config/modelCatalog'

export const SegmentDetailPanel: React.FC = () => {
  const { t } = useTranslation(['pages', 'common'])
  const { segments, activeSegmentId, loadingSegments, updateSegment } = useStoryboard();
  const activeSegment = useMemo(
    () => segments.find((segment) => segment.segmentId === activeSegmentId) ?? null,
    [segments, activeSegmentId]
  );

  const formSubmitRef = useRef<(() => void) | null>(null);
  const formControllerRef = useRef<ModelFormController | null>(null);
  const [schemaSignature, setSchemaSignature] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supportedKeySet = useMemo(() => new Set<string>(SUPPORTED_MODEL_KEYS), []);
  const storeModels = useModelStore((state) => state.models);
  const models = useMemo<ModelItem[]>(() => {
    const filtered = storeModels.filter((item) => supportedKeySet.has(item.value));
    if (filtered.length > 0) {
      return filtered;
    }

    const configs = (SUPPORTED_MODEL_KEYS as readonly string[])
      .map((key) => getModelConfig(key) ?? null)
      .filter((cfg): cfg is NonNullable<ReturnType<typeof getModelConfig>> => cfg !== null);

    if (configs.length === 0) {
      return [];
    }

    const translate = t as unknown as (key: string, options?: { defaultValue?: string }) => string;
    const catalog = buildModelCatalog(configs, translate);
    const fallbackItems: ModelItem[] = [];
    Object.values(catalog.modelsByGroup).forEach((groupModels) => {
      fallbackItems.push(...groupModels);
    });
    return fallbackItems;
  }, [storeModels, supportedKeySet, t]);

  const currentModel = activeSegment?.modelId ?? models[0]?.value ?? '';

  const handleModelChange = useCallback(async (nextModelKey: string) => {
    if (!activeSegment || !nextModelKey) return;
    await updateSegment(activeSegment.segmentId, { modelId: nextModelKey });
  }, [activeSegment, updateSegment]);

  const handleStartGenWithPayload = useCallback(async (payload?: string | Record<string, unknown>) => {
    if (!activeSegment || !payload) return null;
    setGenerating(true);
    setError(null);
    try {
      const config = getModelConfig(currentModel);
      if (!config) throw new Error('模型配置未找到');
      const finalPayload = typeof payload === 'string' ? { body: payload } : applyModelDefaultsToPayload(payload, config);
      const adapter = getAdapter(config.modelKey, config.defaultTaskType);
      if (!adapter) throw new Error('适配器未找到');
      const result = await adapter.generate(finalPayload);
      const values = formControllerRef.current?.getAllValues?.() ?? {};
      const prompt = getPromptValue(values as Record<string, unknown>);
      const taskId = result && typeof result === 'object' && 'requestId' in result
        ? (result.requestId as string)
        : undefined;
      await updateSegment(activeSegment.segmentId, {
        prompt,
        taskId,
        status: 'generating',
        progress: 0,
      });
      return result;
    } catch (err) {
      const extractApiErrorMessage = (error: unknown): string | undefined => {
        if (!error || typeof error !== 'object' || !('body' in error)) return undefined;
        const body = (error as { body?: unknown }).body;
        if (!body || typeof body !== 'object') return undefined;
        const nested = (body as { error?: unknown }).error;
        if (!nested || typeof nested !== 'object') return undefined;
        const messageZh = (nested as { message_zh?: unknown }).message_zh;
        if (typeof messageZh === 'string' && messageZh.trim()) return messageZh;
        const message = (nested as { message?: unknown }).message;
        return typeof message === 'string' && message.trim() ? message : undefined;
      };

      const fallbackMsg = err instanceof Error ? err.message : undefined;
      const msg = extractApiErrorMessage(err) ?? fallbackMsg ?? String(err);
      setError(msg);
      return null;
    } finally {
      setGenerating(false);
    }
  }, [activeSegment, currentModel, updateSegment]);

  const config = getModelConfig(currentModel);
  const dockContentNode = useMemo(() => {
    if (!activeSegment || !config?.workspace?.form) return null;
    return (
      <ModelFormRouter
        modelKey={currentModel}
        config={config}
        startGenWithPayload={handleStartGenWithPayload}
        generating={generating}
        generationError={error}
        useIconLabels
        externalSubmitRef={formSubmitRef}
        controllerRef={formControllerRef}
        hideSchemaSubmit
        externalTopRow
        onSchemaResolved={(s) => {
          const id = (s?.meta?.id ?? '') as string;
          const ver = (s?.meta?.version ?? '') as string;
          const model = (s?.meta?.modelKey ?? '') as string;
          const fieldsLen = Array.isArray((s as any)?.fields) ? (s as any).fields.length : 0;
          setSchemaSignature(`${model}|${id}|${ver}|${fieldsLen}`);
        }}
      />
    );
  }, [activeSegment, currentModel, config, handleStartGenWithPayload, generating, error]);

  const handlePromptSubmit = useCallback(() => {
    formSubmitRef.current?.();
  }, []);

  const renderContent = () => {
    if (loadingSegments) {
      return (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-sm">正在读取单元详情…</div>
        </div>
      )
    }

    if (!activeSegment) {
      return (
        <div className="flex-1 flex items-center justify-center text-center text-muted-foreground">
          <div className="text-sm">请选择或创建一个单元，开始配置视频创作参数。</div>
        </div>
      )
    }

    return (
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div
          className="overflow-y-auto pr-0 md:pr-1 pb-1 flex-1"
          style={{ paddingBottom: 'calc(var(--prompt-dock-height, 0px) + 0.25rem)' }}
        >
          <div className="min-h-full space-y-3">
            <SegmentGenerationSection segment={activeSegment} />
          </div>
        </div>
        {dockContentNode && (
          <PromptDock
            position="floating"
            content={dockContentNode}
            onSubmit={handlePromptSubmit}
            submitting={generating}
            models={models}
            currentModel={currentModel}
            onModelChange={handleModelChange}
            controllerRef={formControllerRef}
            schemaSignature={schemaSignature}
          />
        )}
      </div>
    )
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">{renderContent()}</div>
  );
};

export default SegmentDetailPanel;
